import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from './lib/supabase'
import { initSwMessageHandler } from './lib/swMessageHandler'
import { setSentryUser } from './lib/sentry'
import { identifyUser, resetUser } from './lib/posthog'

import DashboardLayout   from './components/layout/DashboardLayout'
import LandingPage       from './pages/LandingPage'
import SignIn            from './pages/SignIn'
import SignUp            from './pages/SignUp'
import Onboarding        from './pages/Onboarding'
import HomePage          from './pages/HomePage'
import ListPage          from './pages/ListPage'
import UnpackPage        from './pages/UnpackPage'
import SavingsPage       from './pages/SavingsPage'
import HouseholdPage     from './pages/HouseholdPage'
import Join              from './pages/Join'
import JoinHousehold    from './pages/JoinHousehold'
import Paywall           from './pages/Paywall'
import Settings          from './pages/Settings'
import StorePricesPage   from './pages/StorePricesPage'
import BudgetDetailsPage from './pages/BudgetDetailsPage'
import PriceHistoryPage  from './pages/PriceHistoryPage'
import GuestShopper      from './pages/GuestShopper'
import ResetPassword     from './pages/ResetPassword'
import YearReviewPage    from './pages/YearReviewPage'
import Shop              from './pages/Shop'
import More              from './pages/More'
import QuickAdd          from './pages/QuickAdd'
import MealPlanningPage  from './pages/MealPlanning'
import Recipes           from './pages/Recipes'
import Spending          from './pages/Spending'
import HowItWorks        from './pages/HowItWorks'
import AlexaAuth         from './pages/AlexaAuth'
import Privacy                from './pages/Privacy'
import RecipeSuggestionDetail from './pages/RecipeSuggestionDetail'
import Support                from './pages/Support'
import Terms                  from './pages/Terms'
import RefundPolicy           from './pages/RefundPolicy'
import InstallIos             from './pages/InstallIos'
import DeleteAccount          from './pages/DeleteAccount'
import ConfirmDeletion        from './pages/ConfirmDeletion'
import DebugLogs              from './pages/DebugLogs'

// ── AuthGate ─────────────────────────────────────────────────────────────────
// checkedRef ensures the auth+onboarding check runs ONCE on mount only.
// Re-renders from navigation never re-trigger it, breaking the redirect loop.

function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{
    ready:         boolean
    userId:        string | null
    onboardingDone: boolean
  }>({ ready: false, userId: null, onboardingDone: false })

  const location    = useLocation()
  const navigate    = useNavigate()
  const checkedRef  = useRef(false)

  // Bridge for SW notificationclick → SPA route changes. Idempotent across
  // remounts; module-level guard prevents double-attach.
  useEffect(() => {
    initSwMessageHandler(navigate)
  }, [navigate])

  useEffect(() => {
    if (checkedRef.current) return
    checkedRef.current = true

    // Helper: bind the current user across observability tools. household_id
    // is on a separate table (`users`), so we fetch it before identifying so
    // event filtering by household works in PostHog. Failures here are
    // non-fatal — analytics shouldn't ever break sign-in.
    async function identifyForObservability(authUser: { id: string; email?: string }) {
      let householdId: string | undefined
      try {
        const { data } = await supabase
          .from('users')
          .select('household_id')
          .eq('id', authUser.id)
          .maybeSingle()
        householdId = (data?.household_id as string | undefined) ?? undefined
      } catch { /* swallow — telemetry must not block auth */ }

      setSentryUser({ id: authUser.id, email: authUser.email, household_id: householdId })
      identifyUser({ id: authUser.id, email: authUser.email, household_id: householdId })
    }

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setState({
          ready:          true,
          userId:         session?.user?.id ?? null,
          onboardingDone: true,
        })
        if (session?.user) {
          identifyForObservability({
            id:    session.user.id,
            email: session.user.email ?? undefined,
          })
        }
      } catch (err) {
        console.error('AuthGate error:', err)
        setState({ ready: true, userId: null, onboardingDone: true })
      }
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setState({ ready: true, userId: null, onboardingDone: false })
          setSentryUser(null)
          resetUser()
          return
        }

        // Token refresh failed — clear session and force re-login
        if (event === 'TOKEN_REFRESHED' && !session) {
          await supabase.auth.signOut()
          window.location.href = '/sign-in'
          return
        }

        if (event === 'SIGNED_IN' && session) {
          setState({
            ready:          true,
            userId:         session.user.id,
            onboardingDone: true,
          })
          identifyForObservability({
            id:    session.user.id,
            email: session.user.email ?? undefined,
          })
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  if (!state.ready) {
    return (
      <div className="min-h-screen bg-[#FAF8F4] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
      </div>
    )
  }

  const path       = location.pathname
  // Logged-out access required for: marketing/auth flows, guest-shop tokens,
  // and the legal/support pages that Apple's app review team needs to reach
  // before signing in.
  const isPublic   = [
                       '/', '/sign-in', '/sign-up', '/reset-password', '/join',
                       '/privacy', '/terms', '/refund-policy', '/support',
                       '/install-ios', '/delete-account', '/confirm-deletion',
                     ].includes(path) || path.startsWith('/shop/')
  const isOnboarding = path === '/onboarding' || path === '/paywall'

  // Not logged in
  if (!state.userId) {
    if (!isPublic) return <Navigate to="/sign-in" replace />
    return <>{children}</>
  }

  // Logged in — not onboarded
  if (!state.onboardingDone && !isOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  // Logged in — onboarded — on an auth/landing page
  if (state.onboardingDone && (path === '/' || path === '/sign-in' || path === '/sign-up')) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

// ── App ───────────────────────────────────────────────────────────────────────

// ── ErrorFallback ──────────────────────────────────────────────────────────
// Shown when the Sentry ErrorBoundary catches a render-time crash. Matches
// the warm-editorial palette so it doesn't feel like a system error.

function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-6">
      <div className="bg-paper border border-border-sand rounded-2xl p-8 max-w-md w-full text-center">
        <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-2">
          Something went wrong
        </div>
        <h1 className="font-serif text-[24px] text-ink leading-[1.15] mb-3">
          We've been <em className="italic">notified</em>.
        </h1>
        <p className="font-serif text-[14px] italic text-ink-muted leading-[1.5] mb-6">
          An unexpected error happened on this screen. Try reloading — your data is safe.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full rounded-2xl bg-forest text-forest-ink py-3 text-[14px] font-bold tracking-[-0.01em]"
        >
          Reload
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <BrowserRouter>
      <AuthGate>
        <Routes>

          {/* ── Public ── */}
          <Route path="/"               element={<LandingPage />} />
          <Route path="/sign-in"        element={<SignIn />} />
          <Route path="/sign-up"        element={<SignUp />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/shop/:token"    element={<GuestShopper />} />
          <Route path="/join"           element={<Join />} />
          <Route path="/join/:token"    element={<JoinHousehold />} />
          <Route path="/privacy"        element={<Privacy />} />
          <Route path="/terms"          element={<Terms />} />
          <Route path="/refund-policy"  element={<RefundPolicy />} />
          <Route path="/support"        element={<Support />} />
          <Route path="/install-ios"    element={<InstallIos />} />
          <Route path="/delete-account"    element={<DeleteAccount />} />
          <Route path="/confirm-deletion"  element={<ConfirmDeletion />} />

          {/* ── Auth required, no bottom nav ── */}
          <Route path="/onboarding"   element={<Onboarding />} />
          <Route path="/paywall"      element={<Paywall />} />
          <Route path="/quick-add"    element={<QuickAdd />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/alexa-auth"   element={<AlexaAuth />} />
          <Route path="/debug-logs"   element={<DebugLogs />} />

          {/* ── Auth required, with bottom nav ── */}
          <Route element={<DashboardLayout />}>
            <Route path="/home"           element={<HomePage />} />
            <Route path="/shop"           element={<Shop />} />
            <Route path="/more"           element={<More />} />
            <Route path="/list"           element={<ListPage />} />
            <Route path="/unpack"         element={<UnpackPage />} />
            <Route path="/savings"        element={<SavingsPage />} />
            <Route path="/household"      element={<HouseholdPage />} />
            <Route path="/settings"       element={<Settings />} />
            <Route path="/store-prices"   element={<StorePricesPage />} />
            <Route path="/budget-details" element={<BudgetDetailsPage />} />
            <Route path="/price-history"  element={<PriceHistoryPage />} />
            <Route path="/year-review"    element={<YearReviewPage />} />
            <Route path="/meal-planning"  element={<MealPlanningPage />} />
            <Route path="/recipes"                    element={<Recipes />} />
            <Route path="/recipe-suggestion/:id"  element={<RecipeSuggestionDetail />} />
            <Route path="/spending"               element={<Spending />} />
          </Route>

          {/* ── Fallback ── */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </AuthGate>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}
